export type RiskLevel = "low" | "moderate" | "high";

export type SafetyDomain =
  | "emotionalAbuse"
  | "financialAbuse"
  | "physicalAbuse"
  | "techAbuse"
  | "stalking"
  | "sexualAbuse"
  | "abuserAccess"
  | "abuserRisk"
  | "perceivedSafety"
  | "depression"
  | "anxiety"
  | "ptsd"
  | "substance";

type Threshold = {
  moderate: number;
  high: number;
};

type DomainLevels = Record<SafetyDomain, RiskLevel>;

export type SafetyPlanItem = {
  domain: SafetyDomain;
  level: Exclude<RiskLevel, "low">;
  score: number;
  actions: string[];
};

export type SafetyPlan = {
  levels: DomainLevels;
  scores: Record<SafetyDomain, number>;
  recommendations: SafetyPlanItem[];
  urgent: boolean;
  urgentActions: string[];
  urgentReasons: string[];
  generalActions: string[];
};

const THRESHOLDS: Record<SafetyDomain, Threshold> = {
  emotionalAbuse: { moderate: 13, high: 30 },
  financialAbuse: { moderate: 15, high: 25 },
  physicalAbuse: { moderate: 1, high: 9 },
  techAbuse: { moderate: 9, high: 17 },
  stalking: { moderate: 6, high: 11 },
  sexualAbuse: { moderate: 1, high: 4 },
  abuserAccess: { moderate: 8, high: 13 },
  abuserRisk: { moderate: 4, high: 8 },
  perceivedSafety: { moderate: 8, high: 16 },
  depression: { moderate: 15, high: 22 },
  anxiety: { moderate: 1, high: 8 },
  ptsd: { moderate: 1, high: 3 },
  substance: { moderate: 4, high: 8 },
};

const ACTIONS: Partial<
  Record<
    SafetyDomain,
    Partial<Record<Exclude<RiskLevel, "low">, string[]>>
  >
> = {
  physicalAbuse: {
    moderate: [
      "safetyPlan.actions.physicalAbuse.moderate.prepareBag",
      "safetyPlan.actions.physicalAbuse.moderate.safeRoutes",
    ],
    high: [
      "safetyPlan.actions.physicalAbuse.high.emergencyServices",
      "safetyPlan.actions.physicalAbuse.high.shelterPlan",
      "safetyPlan.actions.physicalAbuse.high.protectionOrder",
    ],
  },
  abuserAccess: {
    moderate: [
      "safetyPlan.actions.abuserAccess.moderate.changeRoutine",
      "safetyPlan.actions.abuserAccess.moderate.homeSecurity",
    ],
    high: [
      "safetyPlan.actions.abuserAccess.high.relocate",
      "safetyPlan.actions.abuserAccess.high.policeEscort",
      "safetyPlan.actions.abuserAccess.high.limitContact",
    ],
  },
  abuserRisk: {
    moderate: [
      "safetyPlan.actions.abuserRisk.moderate.documentIncident",
      "safetyPlan.actions.abuserRisk.moderate.legalConsult",
    ],
    high: [
      "safetyPlan.actions.abuserRisk.high.alertAuthorities",
      "safetyPlan.actions.abuserRisk.high.scheduleCheckIns",
    ],
  },
  perceivedSafety: {
    moderate: [
      "safetyPlan.actions.perceivedSafety.moderate.safeRooms",
      "safetyPlan.actions.perceivedSafety.moderate.teachChildren",
    ],
    high: [
      "safetyPlan.actions.perceivedSafety.high.relocateNow",
      "safetyPlan.actions.perceivedSafety.high.connectShelter",
    ],
  },
  techAbuse: {
    moderate: [
      "safetyPlan.actions.techAbuse.moderate.resetPasswords",
      "safetyPlan.actions.techAbuse.moderate.securityReview",
    ],
    high: [
      "safetyPlan.actions.techAbuse.high.disableTracking",
      "safetyPlan.actions.techAbuse.high.secureDevices",
    ],
  },
  stalking: {
    moderate: [
      "safetyPlan.actions.stalking.moderate.logIncidents",
      "safetyPlan.actions.stalking.moderate.notifyWorkSchool",
    ],
    high: [
      "safetyPlan.actions.stalking.high.policeReport",
      "safetyPlan.actions.stalking.high.requestProtection",
    ],
  },
  financialAbuse: {
    moderate: [
      "safetyPlan.actions.financialAbuse.moderate.separateFinances",
      "safetyPlan.actions.financialAbuse.moderate.copyDocuments",
    ],
    high: [
      "safetyPlan.actions.financialAbuse.high.freezeCredit",
      "safetyPlan.actions.financialAbuse.high.emergencyFund",
    ],
  },
  emotionalAbuse: {
    moderate: [
      "safetyPlan.actions.emotionalAbuse.moderate.supportNetwork",
      "safetyPlan.actions.emotionalAbuse.moderate.grounding",
    ],
    high: [
      "safetyPlan.actions.emotionalAbuse.high.traumaCounselling",
      "safetyPlan.actions.emotionalAbuse.high.prepareStatement",
    ],
  },
  sexualAbuse: {
    moderate: [
      "safetyPlan.actions.sexualAbuse.moderate.medicalCare",
      "safetyPlan.actions.sexualAbuse.moderate.legalRights",
    ],
    high: [
      "safetyPlan.actions.sexualAbuse.high.emergencyClinic",
      "safetyPlan.actions.sexualAbuse.high.evidenceSupport",
    ],
  },
  depression: {
    moderate: [
      "safetyPlan.actions.depression.moderate.therapy",
      "safetyPlan.actions.depression.moderate.monitorMood",
    ],
    high: [
      "safetyPlan.actions.depression.high.urgentMentalHealth",
      "safetyPlan.actions.depression.high.suicidePlan",
    ],
  },
  anxiety: {
    moderate: [
      "safetyPlan.actions.anxiety.moderate.copingSkills",
      "safetyPlan.actions.anxiety.moderate.checkIns",
    ],
    high: [
      "safetyPlan.actions.anxiety.high.professionalSupport",
      "safetyPlan.actions.anxiety.high.emergencyCalming",
    ],
  },
  ptsd: {
    moderate: [
      "safetyPlan.actions.ptsd.moderate.traumaTherapy",
      "safetyPlan.actions.ptsd.moderate.triggerPlan",
    ],
    high: [
      "safetyPlan.actions.ptsd.high.specialistReferral",
      "safetyPlan.actions.ptsd.high.flashbackSafety",
    ],
  },
  substance: {
    moderate: [
      "safetyPlan.actions.substance.moderate.reduceUse",
      "safetyPlan.actions.substance.moderate.supportGroup",
    ],
    high: [
      "safetyPlan.actions.substance.high.medicalDetox",
      "safetyPlan.actions.substance.high.combinedRisk",
    ],
  },
};

const COMMON_ACTIONS = [
  "safetyPlan.common.actions.emergencyNumbers",
  "safetyPlan.common.actions.sharePlan",
  "safetyPlan.common.actions.reviewMonthly",
];

const URGENT_ACTIONS = [
  "safetyPlan.urgent.actions.call112",
  "safetyPlan.urgent.actions.reachShelter",
  "safetyPlan.urgent.actions.contactTrusted",
];

function getLevel(value: number, threshold: Threshold): RiskLevel {
  if (value >= threshold.high) return "high";
  if (value >= threshold.moderate) return "moderate";
  return "low";
}

function evaluateUrgent(levels: DomainLevels): { urgent: boolean; reasons: string[] } {
  const reasons: string[] = [];

  if (
    levels.physicalAbuse === "high" &&
    levels.abuserAccess === "high" &&
    levels.perceivedSafety === "high"
  ) {
    reasons.push("safetyPlan.urgent.reasons.physicalAccess");
  }

  if (
    levels.physicalAbuse === "high" &&
    levels.abuserRisk === "high"
  ) {
    reasons.push("safetyPlan.urgent.reasons.physicalRisk");
  }

  if (
    levels.stalking === "high" &&
    levels.techAbuse === "high"
  ) {
    reasons.push("safetyPlan.urgent.reasons.stalkingTech");
  }

  return { urgent: reasons.length > 0, reasons };
}

export function deriveSafetyPlan(
  bucketTotals: Record<string, number>,
  rollups: Record<string, number>
): SafetyPlan {
  const scores: Record<SafetyDomain, number> = {
    emotionalAbuse: bucketTotals["emotionalAbuse"] ?? 0,
    financialAbuse: bucketTotals["financialAbuse"] ?? 0,
    physicalAbuse: bucketTotals["physicalAbuse"] ?? 0,
    techAbuse: bucketTotals["techAbuse"] ?? 0,
    stalking: bucketTotals["stalking"] ?? 0,
    sexualAbuse: bucketTotals["sexualAbuse"] ?? 0,
    abuserAccess: rollups["abuser:access"] ?? bucketTotals["abuserAccess"] ?? 0,
    abuserRisk: rollups["abuser:risk"] ?? bucketTotals["abuserRisk"] ?? 0,
    perceivedSafety: rollups["perception:safety"] ?? bucketTotals["perceivedSafety"] ?? 0,
    depression: rollups["emotional:depression"] ?? bucketTotals["depression"] ?? 0,
    anxiety: rollups["emotional:anxiety"] ?? bucketTotals["anxiety"] ?? 0,
    ptsd: rollups["emotional:ptsd"] ?? bucketTotals["ptsd"] ?? 0,
    substance: rollups["emotional:substance"]
      ?? (bucketTotals["substanceUse"] ?? 0) + (bucketTotals["substanceFrequency"] ?? 0),
  };

  const levels = {} as DomainLevels;

  (Object.keys(scores) as SafetyDomain[]).forEach((domain) => {
    const threshold = THRESHOLDS[domain];
    levels[domain] = threshold ? getLevel(scores[domain], threshold) : "low";
  });

  const recommendations: SafetyPlanItem[] = [];

  (Object.keys(scores) as SafetyDomain[]).forEach((domain) => {
    const level = levels[domain];
    if (level === "low") return;
    const actions = ACTIONS[domain]?.[level];
    if (actions && actions.length) {
      recommendations.push({
        domain,
        level,
        score: scores[domain],
        actions,
      });
    }
  });

  const { urgent, reasons: urgentReasons } = evaluateUrgent(levels);

  return {
    levels,
    scores,
    recommendations,
    urgent,
    urgentActions: urgent ? URGENT_ACTIONS : [],
    urgentReasons,
    generalActions: COMMON_ACTIONS,
  };
}
